import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Upload,
  DatePicker,
  Button,
  Radio,
  Space,
} from "antd";
import SheetDrawer from "../../components/sheet/SheetDrawer";
import { InboxOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  ALL_POSITION_OPTIONS,
  positionLabel,
  positionMeta,
} from "../../utils/positionMeta";

const { Dragger } = Upload;
const { Option } = Select;

const HERO_POSITIONS = [
  "CAMPAIGNS",
  "FLIGHT_HOME",
];

const isHeroPosition = (pos) => HERO_POSITIONS.includes(pos);

// Positions with their own artwork size — never included in "All side ads"
const DEDICATED_POSITIONS = [
  "AUTHENTICATION",
  "FLIGHT_RESULTS_IN_LIST",
  "ACCOUNT_TRAVEL_HUB",
  "ACCOUNT_MY_TRIPS",
  "INSURANCE_PROMO",
];

const ALL_POSITION_VALUES = ALL_POSITION_OPTIONS.map((opt) => opt.value);
const SIDE_POSITIONS = ALL_POSITION_VALUES.filter(
  (v) => !isHeroPosition(v) && !DEDICATED_POSITIONS.includes(v)
);

// order-independent set comparison, used to detect whether an existing ad's
// positions array matches one of the presets when opening the edit drawer
const sameSet = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((v) => setA.has(v));
};

const LOCATION_OPTIONS = [
  { value: "BEN", label: "Benin" },
  { value: "BFA", label: "Burkina Faso" },
  { value: "CIV", label: "Côte d'Ivoire" },
  { value: "CMR", label: "Cameroon" },
  { value: "GHA", label: "Ghana" },
  { value: "GIN", label: "Guinea" },
  { value: "MLI", label: "Mali" },
  { value: "NER", label: "Niger" },
  { value: "SEN", label: "Senegal" },
  { value: "TGO", label: "Togo" },
  { value: "USA", label: "United States" },
  { value: "CAN", label: "Canada" },
  { value: "GBR", label: "United Kingdom" },
  { value: "NGA", label: "Nigeria" },
];

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

/**
 * Shared drawer for both creating and editing an ad.
 *
 * mode: "create" | "edit"
 * initialAd: required when mode === "edit" — the ad object from the list
 * prefillPosition: optional — when opened via a specific position's "Add"
 *   button, pre-selects that position in "specific" mode (still editable)
 * onSubmit(formData): called with a FormData ready to send to the API
 */
const AdvertFormDrawer = ({
  open,
  mode,
  initialAd,
  prefillPosition,
  onClose,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [uploadedImage, setUploadedImage] = useState(null);
  const [positionMode, setPositionMode] = useState("specific"); // "specific" | "side" | "hero"
  const [locationMode, setLocationMode] = useState("specific"); // "specific" | "all"
  const [hasEndDate, setHasEndDate] = useState(true);
  const [hasLink, setHasLink] = useState(true);
  const [legacyWildcardPosition, setLegacyWildcardPosition] = useState(false);

  const selectedPositions = Form.useWatch("positions", form) || [];
  const isEdit = mode === "edit";

  // Category restriction checks (only relevant in "specific" mode)
  const hasHeroSelected = selectedPositions.some((p) => isHeroPosition(p));
  const hasStandardSelected = selectedPositions.some((p) => !isHeroPosition(p));

  const isOptionDisabled = (optionValue) => {
    if (selectedPositions.length === 0) return false;
    if (hasHeroSelected && !isHeroPosition(optionValue)) return true;
    if (hasStandardSelected && isHeroPosition(optionValue)) return true;
    return false;
  };

  const getImageRecommendation = () => {
    const positions =
      positionMode === "side"
        ? SIDE_POSITIONS
        : positionMode === "hero"
          ? HERO_POSITIONS
          : selectedPositions;
    const sized = positions.map(positionMeta).filter((meta) => meta?.size);

    // Every chosen position has a measured size: name it (or each of them)
    if (positions.length && sized.length === positions.length) {
      const sizes = [...new Set(sized.map((meta) => meta.size))];
      return sizes.length === 1
          ? `Recommended size: ${sizes[0]} px`
          : `Recommended sizes: ${sizes
              .map(
                (size) =>
                  `${size} px (${sized
                    .filter((meta) => meta.size === size)
                    .map((meta) => meta.label)
                    .join(", ")})`
              )
              .join("; ")}`;
    }

    if (positionMode === "hero") {
      return "Recommended size: 1400 × 886 px (Hero / Dashboard format)";
    }
    if (positionMode === "side") {
      return "Recommended size: 400 px width (Auto height / Sidebar format)";
    }
    if (hasHeroSelected) {
      return "Recommended size: 1400 × 886 px (Hero / Dashboard format)";
    }
    if (hasStandardSelected) {
      return "Recommended size: 400 px width (Auto height / Sidebar format)";
    }
    return "Recommended size: 1400×886 px (Hero) or 400px width (Standard)";
  };

  useEffect(() => {
    if (!open) return;

    if (isEdit && initialAd) {
      const positions = initialAd.positions || [];
      const isWildcardPosition = positions.includes("*");
      const isWildcardLocation = initialAd.locations?.includes("*");

      setLegacyWildcardPosition(isWildcardPosition);

      let resolvedPositionMode = "specific";
      if (!isWildcardPosition) {
        if (sameSet(positions, SIDE_POSITIONS)) resolvedPositionMode = "side";
        else if (sameSet(positions, HERO_POSITIONS)) resolvedPositionMode = "hero";
      }
      setPositionMode(resolvedPositionMode);
      setLocationMode(isWildcardLocation ? "all" : "specific");
      setHasEndDate(!!initialAd.durationTo);
      setHasLink(!!initialAd.fullLink);
      setUploadedImage(null);

      form.setFieldsValue({
        name: initialAd.name,
        organization: initialAd.organization,
        fullLink: initialAd.fullLink,
        // wildcard/preset positions aren't real Select values — leave the
        // manual picker empty in those cases rather than crash on "*"
        positions: resolvedPositionMode === "specific" && !isWildcardPosition ? positions : [],
        weight: initialAd.weight || 1,
        paid: !!initialAd.paid,
        locations: isWildcardLocation ? [] : initialAd.locations,
        durationFrom: initialAd.durationFrom ? moment(initialAd.durationFrom) : null,
        durationTo: initialAd.durationTo ? moment(initialAd.durationTo) : null,
      });
    } else {
      form.resetFields();
      setUploadedImage(null);
      setHasEndDate(false);
      setHasLink(true);
      setLegacyWildcardPosition(false);
      if (prefillPosition) {
        setPositionMode("specific");
        form.setFieldsValue({ positions: [prefillPosition], weight: 1, paid: false });
      } else {
        setPositionMode("specific");
        form.setFieldsValue({ weight: 1, paid: false });
      }
      setLocationMode("all");
    }
  }, [open, mode, initialAd, prefillPosition, form, isEdit]);

  const handleFinish = (values) => {
    const formData = new FormData();

    formData.append("name", values.name);
    formData.append("organization", values.organization);
    if (hasLink && values.fullLink) {
      formData.append("fullLink", values.fullLink);
    } else if (isEdit && !hasLink) {
      formData.append("hasLink", "false");
    }

    let positions;
    if (positionMode === "side") {
      positions = SIDE_POSITIONS;
    } else if (positionMode === "hero") {
      positions = HERO_POSITIONS;
    } else {
      positions = values.positions || [];
    }
    formData.append("positions", JSON.stringify(positions));

    const locations = locationMode === "all" ? ["*"] : values.locations || [];
    formData.append("locations", JSON.stringify(locations));

    formData.append("weight", String(values.weight || 1));
    formData.append("paid", values.paid ? "true" : "false");

    if (values.durationFrom) {
      formData.append("durationFrom", values.durationFrom.format("YYYY-MM-DD"));
    }
    if (hasEndDate && values.durationTo) {
      formData.append("durationTo", values.durationTo.format("YYYY-MM-DD"));
    } else if (isEdit && !hasEndDate) {
      formData.append("clearDurationTo", "true");
    }

    if (uploadedImage) {
      formData.append("image", uploadedImage);
    }

    onSubmit(formData);
  };

  return (
    <SheetDrawer
      title={isEdit ? "Edit Ad" : "Create New Ad"}
      width={640}
      open={open}
      onClose={onClose}
      styles={{ body: { paddingBottom: 80 } }}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item label="Image" required={!isEdit}>
          <Dragger
            name="image"
            accept=".jpg,.png,.jpeg,.gif,.webp"
            showUploadList={false}
            beforeUpload={(file) => {
              if (file.size > MAX_IMAGE_BYTES) {
                form.setFields([
                  { name: "image", errors: ["Image must be 2MB or smaller"] },
                ]);
                return Upload.LIST_IGNORE;
              }
              setUploadedImage(file);
              return false;
            }}
            onChange={(info) => {
              if (info.file.status === "removed") setUploadedImage(null);
            }}
          >
            {uploadedImage ? (
              <img
                src={URL.createObjectURL(uploadedImage)}
                alt="Preview"
                style={{ width: "100%", maxHeight: 160, objectFit: "contain" }}
              />
            ) : isEdit && initialAd?.image ? (
              <img
                src={initialAd.image}
                alt="Current"
                style={{ width: "100%", maxHeight: 160, objectFit: "contain" }}
              />
            ) : (
              <>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag an image to upload</p>
                <p className="ant-upload-hint" style={{ fontSize: 12, color: "#666", padding: "0 16px" }}>
                  <strong>{getImageRecommendation()}</strong>
                </p>
              </>
            )}
          </Dragger>
          {isEdit && (
            <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
              Leave blank to keep the current image.
            </div>
          )}
        </Form.Item>

        <Form.Item
          label="Ad name"
          name="name"
          rules={[{ required: true, message: "Please enter the ad's name" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Organization"
          name="organization"
          rules={[{ required: true, message: "Please enter the organization" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item label="Destination link">
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Space align="center">
              <Switch checked={hasLink} onChange={setHasLink} />
              <span style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.88)" }}>
                {hasLink ? "Opens link in a new tab" : "Display only (not clickable)"}
              </span>
            </Space>

            {hasLink && (
              <Form.Item
                name="fullLink"
                noStyle
                rules={[
                  { required: true, message: "Please enter the destination link" },
                  {
                    pattern: /^(https?|ftp):\/\/.+$/,
                    message: "Must start with https://, http://, or ftp://",
                  },
                ]}
              >
                <Input placeholder="https://example.com/promo" />
              </Form.Item>
            )}
          </Space>
        </Form.Item>

        <Form.Item label="Positions">
          {legacyWildcardPosition && (
            <div style={{ fontSize: 12, color: "#d97706", marginBottom: 8 }}>
              This ad currently uses the old "all positions" setting. Please choose
              "All side ads", "All hero ads", or select specific positions to update it.
            </div>
          )}

          <Radio.Group
            value={positionMode}
            onChange={(e) => {
              setPositionMode(e.target.value);
              if (e.target.value !== "specific") {
                form.setFieldValue("positions", []);
              }
            }}
            style={{ marginBottom: 12 }}
          >
            <Radio.Button value="specific">Specific positions</Radio.Button>
            <Radio.Button value="side">All side ads</Radio.Button>
            <Radio.Button value="hero">All hero ads</Radio.Button>
          </Radio.Group>

          {positionMode === "specific" && (
            <>
              <Form.Item
                name="positions"
                noStyle
                rules={[
                  {
                    validator: (_, value) =>
                      value && value.length
                        ? Promise.resolve()
                        : Promise.reject(new Error("Select at least one position")),
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select one or more positions"
                >
                  {ALL_POSITION_OPTIONS.map((opt) => (
                    <Option
                      key={opt.value}
                      value={opt.value}
                      disabled={isOptionDisabled(opt.value)}
                    >
                      {opt.label}{" "}
                      {opt.size
                        ? `(${opt.size})`
                        : isHeroPosition(opt.value)
                          ? "(1400×886)"
                          : "(400px)"}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {selectedPositions.length > 0 && (
                <div style={{ fontSize: 12, color: "#d97706", marginTop: 6 }}>
                  {hasHeroSelected
                    ? "Hero positions selected. Standard placements disabled for this ad."
                    : "Standard positions selected. Hero placements disabled for this ad."}{" "}
                  {getImageRecommendation()}.
                </div>
              )}
            </>
          )}

          {positionMode === "side" && (
  <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
    This ad will appear on every standard (non-hero) position:{" "}
    {SIDE_POSITIONS.map(positionLabel).join(", ")}. {getImageRecommendation()}.
  </div>
)}

{positionMode === "hero" && (
  <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
    This ad will appear on every hero position:{" "}
    {HERO_POSITIONS.map(positionLabel).join(", ")}. {getImageRecommendation()}.
  </div>
)}
        </Form.Item>

        <Form.Item label="Locations">
          <Radio.Group
            value={locationMode}
            onChange={(e) => setLocationMode(e.target.value)}
            style={{ marginBottom: 12 }}
          >
            <Radio.Button value="all">All locations</Radio.Button>
            <Radio.Button value="specific">Specific countries</Radio.Button>
          </Radio.Group>

          {locationMode === "specific" && (
            <Form.Item
              name="locations"
              noStyle
              rules={[
                {
                  validator: (_, value) =>
                    locationMode === "all" || (value && value.length)
                      ? Promise.resolve()
                      : Promise.reject(new Error("Select at least one country")),
                },
              ]}
            >
              <Select mode="multiple" placeholder="Select one or more countries">
                {LOCATION_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Form.Item>

        <Form.Item
          label="Weight"
          name="weight"
          tooltip="Relative chance of winning rotation against other ads sharing the same position. Higher = shown more often."
        >
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="Paid" name="paid" valuePropName="checked">
          <Switch checkedChildren="Paid" unCheckedChildren="Free" />
        </Form.Item>

        <Form.Item label="Start date" name="durationFrom">
          <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="End date">
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Space align="center">
              <Switch checked={hasEndDate} onChange={setHasEndDate} />
              <span style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.88)" }}>
                {hasEndDate ? "Has end date" : "Runs until turned off"}
              </span>
            </Space>

            {hasEndDate && (
              <Form.Item name="durationTo" noStyle>
                <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
              </Form.Item>
            )}
          </Space>
        </Form.Item>

        <Form.Item style={{ marginTop: 24 }}>
          <Button type="primary" htmlType="submit" block>
            {isEdit ? "Save Changes" : "Create Ad"}
          </Button>
        </Form.Item>
      </Form>
    </SheetDrawer>
  );
};

export default AdvertFormDrawer;